/**
 * How internal values are shown to people.
 *
 * Map ids and room phases are names the code uses. Players should see words
 * instead, and every page that shows one should show the same word, so the
 * formatting lives here rather than in each page.
 */

import type { RoomPhase } from '@overcooked/shared'

/**
 * `galley-10x6` -> `Galley`, `my_big-kitchen` -> `My big kitchen`.
 *
 * The size suffix is dropped because pages that need the size show it
 * separately. A name that is nothing but a size is kept as it is, so it never
 * turns into an empty string.
 */
export function prettyMapName(name: string): string {
  const stripped = name.replace(/-\d+x\d+$/, '')
  const base = (stripped || name).replace(/[-_]+/g, ' ').trim()
  if (!base) return name
  return base.charAt(0).toUpperCase() + base.slice(1)
}

const PHASE_WORDS: Record<RoomPhase, string> = {
  lobby: 'Waiting to start',
  playing: 'Playing',
  ended: 'Round over',
}

/** `lobby` -> `Waiting to start`. */
export function phaseWord(phase: RoomPhase): string {
  return PHASE_WORDS[phase] ?? phase
}

/** What the end card shows. `score: null` is a round this page did not see end. */
export interface EndCard {
  score: number | null
  reason: 'timer' | 'aborted'
}

/**
 * Whether to show the end card, and with what.
 *
 * The feed's own `ended` is the usual answer — it saw `ROUND_ENDED`, or its
 * clock ran out. But a page that arrives in a room whose round is already over
 * (a reload on the scoreboard, a reopened tab, a partner arriving late) has a
 * fresh feed that never saw the whistle, while the room says `ended`. Neither
 * the lobby nor the kitchen is drawn in that phase, so it used to be a blank
 * page with no way to start another round. It gets the card, without a score
 * it never saw, and the same Play again.
 */
export function endCardFor(
  phase: RoomPhase,
  ended: { reason: 'timer' | 'aborted'; score: number } | null,
): EndCard | null {
  if (ended) return { score: ended.score, reason: ended.reason }
  if (phase === 'ended') return { score: null, reason: 'timer' }
  return null
}
